import { Dialog } from "@headlessui/react"
import React, { FC, useEffect, useMemo, useRef, useState } from "react"
import { X } from "heroicons-react"
import { toast } from "react-toastify"
import { KButton, KInput, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { marketingMilestoneEvents } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import Loading from "../../../ui/Loading"

type TrackedUser = {
  id: number
  name: string
  username: string
  track_count: number
  crawl_chartmetric: boolean
}

type Props = {
  open: boolean
  onClose: () => void
}

const TrackedUsersModal: FC<Props> = ({ open, onClose }) => {
  const [search, setSearch] = useState("")
  const [users, setUsers] = useState<TrackedUser[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  // Selection is seeded from the currently-flagged users on the FIRST load only.
  // Search re-fetches must not touch the selection, or unchecking a flagged user would
  // "come back" on the next keystroke.
  const seededRef = useRef(false)

  const fetchUsers = (term: string) => {
    setLoading(true)
    const params = new URLSearchParams({ limit: "500" })
    if (term) params.set("search", term)
    marketingMilestoneEvents(`/tracked_users/?${params.toString()}`, "get")
      .then((resp) => {
        const results = (resp.data?.results as TrackedUser[]) || []
        setUsers(results)
        if (!seededRef.current) {
          seededRef.current = true
          setSelected(new Set(results.filter((u) => u.crawl_chartmetric).map((u) => u.id)))
        }
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        toast.error(err?.response?.data?.detail || "Error loading users", errorFilledToastOptions)
      })
  }

  useEffect(() => {
    if (!open) {
      seededRef.current = false
      return
    }
    setSearch("")
    setSelected(new Set())
    fetchUsers("")
  }, [open])

  useEffect(() => {
    if (!open) return
    const timeout = setTimeout(() => fetchUsers(search), 350)
    return () => clearTimeout(timeout)
  }, [search])

  const allVisibleSelected = useMemo(
    () => users.length > 0 && users.every((u) => selected.has(u.id)),
    [users, selected]
  )

  const toggle = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  const toggleAllVisible = () => {
    const next = new Set(selected)
    if (allVisibleSelected) users.forEach((u) => next.delete(u.id))
    else users.forEach((u) => next.add(u.id))
    setSelected(next)
  }

  const save = () => {
    setSaving(true)
    marketingMilestoneEvents("/set_tracked_users/", "put", { user_ids: Array.from(selected) })
      .then((resp) => {
        setSaving(false)
        toast.success(
          `${resp.data.tracked_count} artist(s) marked for weekly Chartmetric tracking.`,
          successFilledToastOptions
        )
        onClose()
      })
      .catch((err) => {
        setSaving(false)
        toast.error(err?.response?.data?.detail || "Error saving", errorFilledToastOptions)
      })
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-2xl w-[560px] max-w-full p-6 relative">
          <button className="absolute top-4 right-4" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>

          {saving && <Loading />}

          <KTitleSpan
            text="Chartmetric Tracked Artists"
            fontSize={20}
            color="#111"
            lineHeight="28px"
            fontWeight={600}
          />
          <div className="mt-1 mb-4">
            <KSpan
              text="Select which artists (users) the weekly Chartmetric job refreshes. Saving only flags them — it does not run a scan now."
              fontSize={13}
              color="#666"
            />
          </div>

          <div className="mb-3">
            <KInput
              value={search}
              placeholder="Search artist"
              onChange={setSearch}
              height={40}
              leftIcon="/search.svg"
            />
          </div>

          <div className="flex items-center justify-between mb-2 px-1">
            <KSpan text={`${selected.size} tracked`} fontSize={13} fontWeight={500} color="#111" />
          </div>

          <div className="border border-gray-100 rounded-xl max-h-[340px] overflow-y-auto divide-y divide-gray-50">
            {loading && <div className="py-8 text-center text-sm text-gray-400">Loading…</div>}
            {!loading && users.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-400">No artists found</div>
            )}
            {!loading &&
              users.map((user) => (
                <label key={user.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={selected.has(user.id)} onChange={() => toggle(user.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{`${user.name} (${user.username})`}</p>
                    <p className="text-xs text-gray-400">
                      {user.track_count} track{user.track_count === 1 ? "" : "s"}
                    </p>
                  </div>
                </label>
              ))}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <KButton
              text="Cancel"
              height="36px"
              width="100px"
              background="#FFF"
              hoverBackground="#F5F5F5"
              onClick={onClose}
            />
            <KButton
              text="Save"
              height="36px"
              width="120px"
              background="#111"
              hoverBackground="#333"
              textColor="#FFF"
              onClick={save}
              disabled={saving}
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default TrackedUsersModal
